/**
 * Collaboration relay client (Feature #5).
 * Connects to the RYFE WebSocket relay for a board (room = file UUID). The
 * server ACL-gates the join (read) and edit (write) via the central guard; this
 * client just sends/receives scene deltas. The initial snapshot is loaded via
 * the normal (guard-gated) file-content endpoint, not here.
 */

import { wsBase } from "./config";

export interface CollabHandlers {
  onJoined?: (info: { canWrite: boolean }) => void;
  onUpdate?: (payload: unknown, from?: string) => void;
  // A new peer joined the room; writers should resend their current scene so
  // the newcomer picks up unsaved live edits.
  onPeerJoined?: () => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;
}

export interface CollabConnection {
  sendUpdate: (payload: unknown) => void;
  close: () => void;
  readonly canWrite: boolean;
}

/** Open a collab connection for a file. */
export function connectCollab(
  fileId: string,
  handlers: CollabHandlers,
  WebSocketImpl: typeof WebSocket = WebSocket,
): CollabConnection {
  const url = `${wsBase()}/api/collab?fileId=${encodeURIComponent(fileId)}`;
  const ws = new WebSocketImpl(url);
  let canWrite = false;

  ws.addEventListener("message", (ev: MessageEvent) => {
    let msg: {
      type?: string;
      payload?: unknown;
      from?: string;
      canWrite?: boolean;
    };
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    if (msg.type === "joined") {
      canWrite = !!msg.canWrite;
      handlers.onJoined?.({ canWrite });
    } else if (msg.type === "update") {
      handlers.onUpdate?.(msg.payload, msg.from);
    } else if (msg.type === "peer-joined") {
      handlers.onPeerJoined?.();
    }
  });
  ws.addEventListener("close", () => handlers.onClose?.());
  ws.addEventListener("error", (e) => handlers.onError?.(e));

  return {
    get canWrite() {
      return canWrite;
    },
    sendUpdate(payload: unknown) {
      if (ws.readyState === ws.OPEN) {
        ws.send(JSON.stringify({ type: "update", payload }));
      }
    },
    close() {
      ws.close();
    },
  };
}
